import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, Users, ShoppingCart, Settings, Package, Tag, User, Menu, X, LogOut, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
interface AdminLayoutProps {
  children: React.ReactNode;
}
export function AdminLayout({
  children
}: AdminLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const location = useLocation();
  const navigation = [{
    group: 'MODIFY PROFILE',
    items: [{
      name: 'Modify Profile',
      href: '/profile',
      icon: User
    }]
  }, {
    group: 'COURSE ADMIN',
    items: [{
      name: 'Manage Courses',
      href: '/courses',
      icon: BookOpen
    }, {
      name: 'Course Sections',
      href: '/sections',
      icon: BookOpen
    }, {
      name: 'Export Course',
      href: '/export-course',
      icon: BookOpen
    }, {
      name: 'Export Users',
      href: '/export-users',
      icon: Users
    }, {
      name: 'Manage Questions',
      href: '/questions',
      icon: BookOpen
    }]
  }, {
    group: 'MANAGEMENT',
    items: [{
      name: 'Users',
      href: '/users',
      icon: Users
    }, {
      name: 'Orders',
      href: '/orders',
      icon: ShoppingCart
    }, {
      name: 'Admin Area',
      href: '/admin-area',
      icon: Settings
    }]
  }, {
    group: 'PACKAGES',
    items: [{
      name: 'Modify Packages',
      href: '/packages',
      icon: Package
    }, {
      name: 'Modify Coupons',
      href: '/coupons',
      icon: Tag
    }]
  }];
  return <div className="min-h-screen bg-slate-50 flex flex-col" data-id="element-82">
      {/* Top Bar */}
      <header className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30" data-id="element-83">
        <div className="flex items-center gap-4" data-id="element-84">
          <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="p-2 hover:bg-slate-100 rounded-md lg:hidden" data-id="element-85">
            <Menu className="h-6 w-6 text-slate-600" data-id="element-86" />
          </button>
          <h1 className="text-lg font-semibold text-slate-800 hidden sm:block" data-id="element-87">
            Welcome to Administration Area
          </h1>
        </div>

        <div className="flex items-center gap-4" data-id="element-88">
          <span className="text-sm text-slate-600 hidden sm:inline-block" data-id="element-89">
            Logged in as{' '}
            <span className="font-medium text-slate-900" data-id="element-90">Admin User</span>
          </span>
          <Button variant="outline" size="sm" className="gap-2" data-id="element-91">
            <LogOut className="h-4 w-4" data-id="element-92" />
            Log-off
          </Button>
        </div>
      </header>

      <div className="flex flex-1 relative" data-id="element-93">
        {/* Sidebar */}
        <aside className={`
            fixed inset-y-0 left-0 z-20 w-64 bg-slate-900 text-slate-300 transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-auto
            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `} data-id="element-94">
          <div className="h-full overflow-y-auto p-4" data-id="element-95">
            <div className="flex items-center justify-between mb-8 lg:hidden" data-id="element-96">
              <span className="text-xl font-bold text-white" data-id="element-97">Menu</span>
              <button onClick={() => setIsSidebarOpen(false)} className="p-2 text-slate-400 hover:text-white" data-id="element-98">
                <X className="h-6 w-6" data-id="element-99" />
              </button>
            </div>

            <div className="mb-8 px-2" data-id="element-100">
              <img src="https://cdn.magicpatterns.com/uploads/hjmtLuDEk79UcGarkchXLf/DSS-logo.png" alt="Desert Sage Seminars" className="h-10 w-auto mb-2" data-id="element-101" />
              <p className="text-xs text-slate-500 uppercase tracking-wider" data-id="element-102">
                Desert Sage Seminars
              </p>
            </div>

            <nav className="space-y-8" data-id="element-103">
              {navigation.map(group => <div key={group.group} data-id="element-104">
                  <h3 className="px-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-105">
                    {group.group}
                  </h3>
                  <div className="space-y-1" data-id="element-106">
                    {group.items.map(item => {
                  const isActive = location.pathname === item.href;
                  return <Link key={item.name} to={item.href} className={`
                            group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors
                            ${isActive ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}
                          `} data-id="element-107">
                          <item.icon className={`mr-3 h-5 w-5 flex-shrink-0 ${isActive ? 'text-white' : 'text-slate-400 group-hover:text-white'}`} data-id="element-108" />
                          {item.name}
                        </Link>;
                })}
                  </div>
                </div>)}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-8 w-full" data-id="element-109">
          <div className="max-w-7xl mx-auto" data-id="element-110">{children}</div>
        </main>

        {/* Mobile Overlay */}
        {isSidebarOpen && <div className="fixed inset-0 bg-black/50 z-10 lg:hidden" onClick={() => setIsSidebarOpen(false)} data-id="element-111" />}
      </div>
    </div>;
}